import { format } from 'date-fns';

import { logger } from '@lib/logger';
import { trackEvent } from '@utils/analytics';
import { OrderBase, OrderStateEnum, OrderSummary } from '@lib/types';

import { client } from './client';
import { endpoints } from './endpoints';

type UpdateOrderStatusRequestType =
  | 'SET_ORDER_AS_REJECTED_FOR_ITEM_NOT_AVAILABLE'
  | 'SET_ORDER_AS_REJECTED_FOR_OTHER_REASONS'
  | 'SET_ORDER_AS_REJECTED_FOR_PRICE_CHANGE'
  | 'SET_ORDER_AS_SEEN'
  | 'SET_ORDER_AS_ROUTED'
  | 'SET_ORDER_AS_ACCEPTED'
  | 'SET_INSPECTING_ORDER_AS_REJECTED'
  | 'SET_ORDER_AS_OUT_FOR_DELIVERY'
  | 'SET_ORDER_AS_FOOD_IS_READY';

interface BaseUpdateOrderStatusData {
  requestType: UpdateOrderStatusRequestType;
  time: number;
  comment?: string;
}

interface UpdateOrderStatus_RejectItemNotAvailable
  extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_REJECTED_FOR_ITEM_NOT_AVAILABLE';
  ordersUUIDs?: string[];
  actionType?: string;
  unAvailableItems: UnAvailableItem[];
}

interface UpdateOrderStatus_RejectPriceChange
  extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_REJECTED_FOR_PRICE_CHANGE';
  ordersUUIDs?: string[];
  actionType?: string;
  changedItemSizesPrices: ChangedItemSizesPrice[];
}

interface UpdateOrderStatus_RejectOther extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_REJECTED_FOR_OTHER_REASONS';
  ordersUUIDs: string[];
  actionType?: string;
}

interface UpdateOrderStatus_SetSeen extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_SEEN';
  ordersUUIDs: string[];
}

interface UpdateOrderStatus_SetRouted extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_ROUTED';
  ordersUUIDs: string[];
}

interface UpdateOrderStatus_Accept extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_ACCEPTED';
  orders: AcceptOrderInput[];
}

interface UpdateOrderStatus_SetOutForDelivery
  extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_OUT_FOR_DELIVERY';
  ordersUUIDs: string[];
}

interface UpdateOrderStatus_SetFoodIsReady extends BaseUpdateOrderStatusData {
  requestType: 'SET_ORDER_AS_FOOD_IS_READY';
  ordersUUIDs: string[];
}

export type UpdateOrderStatusData =
  | UpdateOrderStatus_RejectItemNotAvailable
  | UpdateOrderStatus_RejectPriceChange
  | UpdateOrderStatus_RejectOther
  | UpdateOrderStatus_SetSeen
  | UpdateOrderStatus_SetRouted
  | UpdateOrderStatus_Accept
  | UpdateOrderStatus_SetOutForDelivery
  | UpdateOrderStatus_SetFoodIsReady;

export type UnAvailableItem = {
  orderUUID: string;
  unavailableItemRequests: UnAvailableItemRequest[];
};

export type UnAvailableItemRequest = {
  orderItemUUID: string;
  unavailabilityInterval: string;
};

export type ChangedItemSizesPrice = {
  orderUUID: string;
  newItemPrice: NewItemPrice[];
};

export type AcceptOrderInput_Fleet = {
  orderUUID: string;
  cookingDuration: number;
};

export type AcceptOrderInput_OD = {
  orderUUID: string;
  deliveryDuration: number;
};

export type AcceptOrderInput = AcceptOrderInput_Fleet | AcceptOrderInput_OD;

export interface NewItemPrice {
  orderItemUUID: string;
  newPrice: number;
}

export enum ItemUnavailabilityIntervalEnum {
  HOUR = 'HOUR',
  TWO_HOURS = 'TWO_HOURS',
  FOUR_HOURS = 'FOUR_HOURS',
  NEXT_DAY = 'NEXT_DAY',
  TWO_DAYS = 'TWO_DAYS',
  THREE_DAYS = 'THREE_DAYS',
  FOREVER = 'FOREVER',
}
export enum OrderActionTypeEnum {
  REJECTED_ADDRESS_NOT_CLEAR = 'REJECTED_ADDRESS_NOT_CLEAR',
  REJECTED_ITEMS_UNAVAILABLE = 'REJECTED_ITEMS_UNAVAILABLE',
  REJECTED_PHONE_UNREACHABLE = 'REJECTED_PHONE_UNREACHABLE',
  REJECTED_WRONG_PRICES = 'REJECTED_WRONG_PRICES',
  REJECTED_ADDRESS_OUT_OF_COVERAGE = 'REJECTED_ADDRESS_OUT_OF_COVERAGE',
  REJECTED_BRANCH_CLOSED = 'REJECTED_BRANCH_CLOSED',
  REJECTED_USER_BLACKLISTED = 'REJECTED_USER_BLACKLISTED',
  REJECTED_OTHER = 'REJECTED_OTHER',
}

enum OrderResponseCodeEnum {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

type updateOrderStatusResponse = {
  state: OrderStateEnum;
  orderResponse: [
    {
      code: OrderResponseCodeEnum;
      order: OrderBase<any, false>;
      errorMessage: null | string;
    },
  ];
};

export async function updateOrderStatus(
  data: UpdateOrderStatusData,
): Promise<updateOrderStatusResponse[]> {
  const response = await client.post(endpoints.UPDATE_ORDER_STATUS, data);
  return response.data;
}

const trackedOrders = new Set<string>();

export async function pendingToRoutedOrders(
  newOrders: OrderSummary[],
  orders: OrderSummary[],
): Promise<void> {
  const pendingOrders = orders.filter(
    (order: OrderSummary) => order.state === 'PENDING',
  );
  const pendingOrdersUUIDs = pendingOrders.map(
    (order: OrderSummary) => order.uuid,
  );

  const newPendingOrders = newOrders.filter(
    (order: OrderSummary) => order.state === 'PENDING',
  );
  const newPendingOrdersUUIDs = newPendingOrders.map(
    (order: OrderSummary) => order.uuid,
  );

  const pendingSet = new Set(pendingOrdersUUIDs);
  const newOrdersOnly = newPendingOrders.filter(
    (order) => !pendingSet.has(order.uuid)
  );

  newOrdersOnly.forEach((order) => {
    if (trackedOrders.has(order.uuid)) return;

    trackedOrders.add(order.uuid);

    trackEvent('Order Received', {
      order_short_code: order.shortCode,
      received_at: format(new Date(), 'yyyy-MM-dd hh:mm:ss a'),
      placed_at: format(order.createdAt, 'yyyy-MM-dd hh:mm:ss a'),
    });
  });

  const uuids = newPendingOrdersUUIDs.filter(
    (order) => !pendingOrdersUUIDs.includes(order),
  );

  if (uuids.length) {
    try {
      await updateOrderStatus({
        requestType: 'SET_ORDER_AS_ROUTED',
        time: Date.now(),
        ordersUUIDs: uuids,
      });
    } catch (error) {
      logger.error('Failure!', error);
    }
  }
}
